import { useState } from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  CheckCircle,
  XCircle,
  Clock,
  ZoomIn,
  ExternalLink,
  Upload,
} from "lucide-react";
import { useAuthStore } from "@/lib/store/authStore";
import { useVerifySlip, useRejectSlip, useApprovePayment, formatRelativeTime, type PaymentSlip } from "./hooks";

interface PaymentSlipDetailProps {
  slip: PaymentSlip;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const STATUS_CONFIG: Record<
  string,
  { variant: "default" | "secondary" | "destructive" | "outline"; icon: typeof Clock; label: string; className?: string; subtitle?: string }
> = {
  Uploaded: { variant: "outline", icon: Upload, label: "Uploaded", className: "bg-blue-50 text-blue-700 border-blue-200", subtitle: "Awaiting manual payment approval" },
  Pending: { variant: "secondary", icon: Clock, label: "Pending" },
  Verified: { variant: "default", icon: CheckCircle, label: "Verified" },
  Rejected: { variant: "destructive", icon: XCircle, label: "Rejected" },
};

export function PaymentSlipDetail({
  slip,
  open,
  onOpenChange,
}: PaymentSlipDetailProps) {
  const verifyMutation = useVerifySlip();
  const rejectMutation = useRejectSlip();
  const approveMutation = useApprovePayment();
  const [showRejectInput, setShowRejectInput] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [imageZoom, setImageZoom] = useState(false);

  const tenantServerUrl = useAuthStore((s) => s.tenantServerUrl);
  const isPending = slip.status === "Pending";
  const isUploaded = slip.status === "Uploaded";
  const isRejected = slip.status === "Rejected";
  const showActions = isPending || isUploaded || isRejected;
  const config = STATUS_CONFIG[slip.status] ?? STATUS_CONFIG.Pending;
  const StatusIcon = config.icon;

  // Resolve private file URL to absolute tenant server URL
  const slipImageUrl = slip.slip_image?.startsWith("/")
    ? `${tenantServerUrl}${slip.slip_image}`
    : slip.slip_image;

  async function handleVerify() {
    await verifyMutation.mutateAsync(slip.name);
    onOpenChange(false);
  }

  async function handleApprove() {
    await approveMutation.mutateAsync(slip.name);
    onOpenChange(false);
  }

  async function handleReject() {
    await rejectMutation.mutateAsync({
      slipName: slip.name,
      reason: rejectReason,
    });
    setShowRejectInput(false);
    setRejectReason("");
    onOpenChange(false);
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              Payment Slip
              <Badge variant={config.variant} className={cn("ml-2", config.className)}>
                <StatusIcon className="mr-1 h-3 w-3" />
                {config.label}
              </Badge>
            </DialogTitle>
            {config.subtitle && (
              <p className="text-xs text-muted-foreground">{config.subtitle}</p>
            )}
          </DialogHeader>

          <div className="space-y-4 px-6 pb-6">
            {/* Slip image */}
            {slipImageUrl && (
              <div className="relative">
                <button
                  onClick={() => setImageZoom(true)}
                  className="group relative w-full overflow-hidden rounded-sm border"
                >
                  <img
                    src={slipImageUrl}
                    alt="Payment slip"
                    className="w-full object-contain max-h-64"
                  />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/0 transition-colors group-hover:bg-black/10">
                    <ZoomIn className="h-6 w-6 text-white opacity-0 drop-shadow transition-opacity group-hover:opacity-100" />
                  </div>
                </button>
              </div>
            )}

            {/* Order info */}
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-muted-foreground">Order</p>
                <p className="font-medium flex items-center gap-1">
                  {slip.sales_order}
                  <ExternalLink className="h-3 w-3 text-muted-foreground" />
                </p>
              </div>
              <div>
                <p className="text-muted-foreground">Uploaded</p>
                <p className="font-medium">
                  {slip.uploaded_at
                    ? formatRelativeTime(slip.uploaded_at)
                    : "—"}
                </p>
              </div>
              {slip.verified_by && (
                <div>
                  <p className="text-muted-foreground">Verified by</p>
                  <p className="font-medium">{slip.verified_by}</p>
                </div>
              )}
              {slip.verified_at && (
                <div>
                  <p className="text-muted-foreground">Verified at</p>
                  <p className="font-medium">
                    {formatRelativeTime(slip.verified_at)}
                  </p>
                </div>
              )}
              {slip.rejection_reason && (
                <div className="col-span-2">
                  <p className="text-muted-foreground">Rejection reason</p>
                  <p className="font-medium text-red-600">
                    {slip.rejection_reason}
                  </p>
                </div>
              )}
            </div>

            {/* Action buttons — for pending and uploaded slips */}
            {showActions && (
              <div className="space-y-2 pt-2">
                {!showRejectInput ? (
                  <div className="flex gap-2">
                    {isUploaded || isRejected ? (
                      <Button
                        onClick={handleApprove}
                        disabled={approveMutation.isPending}
                        className="flex-1"
                      >
                        <CheckCircle className="mr-2 h-4 w-4" />
                        {approveMutation.isPending ? "Approving..." : "Approve Payment"}
                      </Button>
                    ) : (
                      <Button
                        onClick={handleVerify}
                        disabled={verifyMutation.isPending}
                        className="flex-1 bg-green-600 hover:bg-green-700"
                      >
                        <CheckCircle className="mr-2 h-4 w-4" />
                        {verifyMutation.isPending ? "Verifying..." : "Verify"}
                      </Button>
                    )}
                    {!isRejected && (
                      <Button
                        variant="outline"
                        onClick={() => setShowRejectInput(true)}
                        disabled={rejectMutation.isPending}
                        className="flex-1 text-red-600 border-red-200 hover:bg-red-50"
                      >
                        <XCircle className="mr-2 h-4 w-4" />
                        Reject
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Input
                      placeholder="Reason for rejection (optional)"
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                      autoFocus
                    />
                    <div className="flex gap-2">
                      <Button
                        variant="destructive"
                        onClick={handleReject}
                        disabled={rejectMutation.isPending}
                        className="flex-1"
                      >
                        {rejectMutation.isPending
                          ? "Rejecting..."
                          : "Confirm Reject"}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => {
                          setShowRejectInput(false);
                          setRejectReason("");
                        }}
                        className="flex-1"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Full-size image zoom dialog */}
      <Dialog open={imageZoom} onOpenChange={setImageZoom}>
        <DialogContent className="max-w-3xl p-0 overflow-hidden">
          {slipImageUrl && (
            <img
              src={slipImageUrl}
              alt="Payment slip (full size)"
              className="w-full object-contain"
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

